<template>
  <section class="home-page">
    <div class="hero">
      <div class="container hero-grid">
        <div class="hero-copy">
          <p class="hero-eyebrow">{{ tr('home.heroEyebrow') }}</p>
          <h1 class="hero-title">
            {{ tr('home.heroTitle') }}
          </h1>
          <p class="hero-subtitle">
            {{ tr('home.heroSubtitle') }}
          </p>

          <div class="hero-actions">
            <router-link to="/events" custom v-slot="{ navigate }">
              <Button variant="primary" size="lg" @click="() => { trackHeroCTA('events'); navigate() }">
                <i class="fas fa-calendar-days"></i>
                {{ tr('home.ctaEvents') }}
              </Button>
            </router-link>
            <router-link to="/about" custom v-slot="{ navigate }">
              <Button variant="outline" size="lg" @click="() => { trackHeroCTA('about'); navigate() }">
                <i class="fas fa-book-open"></i>
                {{ tr('home.ctaAbout') }}
              </Button>
            </router-link>
          </div>

          <ul class="hero-facts" :aria-label="tr('home.editorialTitle')">
            <li><strong>135+</strong><span>{{ tr('home.factsYears') }}</span></li>
            <li><strong>35</strong><span>{{ tr('home.factsMusicians') }}</span></li>
            <li><strong>{{ tr('nav.school') }}</strong><span>{{ tr('home.factsSchool') }}</span></li>
          </ul>
        </div>

        <div class="hero-visual">
          <figure class="hero-image-frame">
            <img
              :src="heroImage"
              :alt="tr('home.altPerformance')"
              class="hero-image"
              width="1600"
              height="1067"
              fetchpriority="high"
              decoding="async"
            />
            <figcaption>{{ tr('home.imageCaption') }}</figcaption>
          </figure>

          <article class="next-concert-card" :aria-label="tr('home.nextConcertLabel')">
            <p class="next-concert-label">{{ tr('home.nextConcertLabel') }}</p>
            <h2 class="next-concert-title">{{ tr('home.nextConcertTitle') }}</h2>
            <ul class="next-concert-meta">
              <li>
                <i class="fas fa-location-dot"></i>
                <span>{{ tr('home.nextConcertLocation') }}</span>
              </li>
              <li>
                <i class="fas fa-clock"></i>
                <span>{{ tr('home.nextConcertSchedule') }}</span>
              </li>
              <li>
                <i class="fas fa-bell"></i>
                <span>{{ tr('home.nextConcertUpdates') }}</span>
              </li>
            </ul>
            <router-link to="/events" custom v-slot="{ navigate }">
              <Button variant="secondary" size="md" fullWidth @click="() => { trackHeroCTA('next_concert'); navigate() }">
                {{ tr('home.nextConcertButton') }}
              </Button>
            </router-link>
          </article>

          <img
            :src="logoNuevo"
            :alt="tr('home.altLogo')"
            class="hero-badge-image"
            width="1200"
            height="900"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>

    <div class="editorial-section section">
      <div class="container">
        <div class="editorial-header">
          <p class="section-eyebrow">{{ tr('home.editorialEyebrow') }}</p>
          <h2>{{ tr('home.editorialTitle') }}</h2>
          <p>
            {{ tr('home.editorialSubtitle') }}
          </p>
        </div>

        <div class="editorial-grid">
          <article class="editorial-card">
            <div class="editorial-icon"><i class="fas fa-timeline"></i></div>
            <h3>{{ tr('home.cardHistoryTitle') }}</h3>
            <p>
              {{ tr('home.cardHistoryText') }}
            </p>
            <router-link to="/about" @click="trackFeatureClick('about')">
              {{ tr('home.cardHistoryLink') }}
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </article>

          <article class="editorial-card editorial-card-accent">
            <div class="editorial-icon"><i class="fas fa-graduation-cap"></i></div>
            <h3>{{ tr('home.cardSchoolTitle') }}</h3>
            <p>
              {{ tr('home.cardSchoolText') }}
            </p>
            <router-link to="/school" @click="trackFeatureClick('school')">
              {{ tr('home.cardSchoolLink') }}
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </article>

          <article class="editorial-card">
            <div class="editorial-icon"><i class="fas fa-calendar-week"></i></div>
            <h3>{{ tr('home.cardEventsTitle') }}</h3>
            <p>
              {{ tr('home.cardEventsText') }}
            </p>
            <router-link to="/events" @click="trackFeatureClick('events')">
              {{ tr('home.cardEventsLink') }}
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </article>
        </div>
      </div>
    </div>

    <div class="stats-section section">
      <div class="container stats-grid">
        <article class="stat-item">
          <p class="stat-number">1890</p>
          <p class="stat-label">{{ tr('home.statFounding') }}</p>
        </article>
        <article class="stat-item">
          <p class="stat-number">+135</p>
          <p class="stat-label">{{ tr('home.statYears') }}</p>
        </article>
        <article class="stat-item">
          <p class="stat-number">∞</p>
          <p class="stat-label">{{ tr('home.statMoments') }}</p>
        </article>
      </div>
    </div>

    <div class="gallery-section section">
      <div class="container">
        <div class="gallery-head">
          <p class="section-eyebrow">{{ tr('home.galleryEyebrow') }}</p>
          <h2>{{ tr('home.galleryTitle') }}</h2>
        </div>

        <div class="gallery-shell">
          <Carousel
            :itemsToShow="1"
            :autoplay="8000"
            :transition="650"
            :wrapAround="true"
            :mouseDrag="true"
            :touchDrag="true"
            :breakpoints="carouselBreakpoints"
            class="modern-carousel"
          >
            <template #slides>
              <Slide v-for="slide in slides" :key="slide.id">
                <div class="slide-content">
                  <img
                    :src="slide.image"
                    :alt="slide.altKey ? tr(slide.altKey) : tr('home.imageAltDefault')"
                    class="slide-image"
                    :class="{ 'logo-image': slide.isLogo }"
                    loading="lazy"
                    decoding="async"
                    :width="slide.width"
                    :height="slide.height"
                  />
                </div>
              </Slide>
            </template>

            <template #addons="{ slidesCount }">
              <Navigation v-if="slidesCount > 1" />
              <Pagination v-if="slidesCount > 1" />
            </template>
          </Carousel>
        </div>
      </div>
    </div>

    <div class="cta-section section">
      <div class="container">
        <article class="cta-card">
          <div class="cta-content">
            <p class="section-eyebrow section-eyebrow-light">{{ tr('home.ctaEyebrow') }}</p>
            <h2 class="cta-title">{{ tr('home.ctaTitle') }}</h2>
            <p class="cta-text">
              {{ tr('home.ctaText') }}
            </p>
            <router-link to="/contact" custom v-slot="{ navigate }">
              <Button variant="secondary" size="lg" @click="() => { trackMainCTA(); navigate() }">
                <i class="fas fa-envelope-open-text"></i>
                {{ tr('home.ctaButton') }}
              </Button>
            </router-link>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted } from 'vue';
import { useHead } from '@vueuse/head';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel';
import Button from '../ui/Button.vue';
import { useAnalytics } from '../../composables/useAnalytics';
import { buildHeadConfig, getBreadcrumbJsonLd, getMusicGroupJsonLd } from '../../seo/meta';
import logoNuevo from '../../assets/24_07_2025_LogoBandaNuevo.jpg';
import image2 from '../../assets/foto1.png';
import image3 from '../../assets/foto2.png';
import image4 from '../../assets/foto3.png';
import image5 from '../../assets/foto4.png';
import image6 from '../../assets/foto5.png';

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

const { trackEvent } = useAnalytics();

useHead(
  buildHeadConfig('home', {
    jsonLd: [getMusicGroupJsonLd(), getBreadcrumbJsonLd('home')],
  }),
);

const heroImage = image3;

const slides = [
  {
    id: 1,
    image: logoNuevo,
    isLogo: true,
    altKey: 'home.altLogo',
    width: 1200,
    height: 900,
  },
  {
    id: 2,
    image: image3,
    isLogo: false,
    altKey: 'home.altPerformance',
    width: 1600,
    height: 1067,
  },
  {
    id: 3,
    image: image2,
    isLogo: false,
    altKey: 'home.altConcert',
    width: 1600,
    height: 1067,
  },
  {
    id: 4,
    image: image4,
    isLogo: false,
    altKey: 'home.altEvent',
    width: 1600,
    height: 1067,
  },
  {
    id: 5,
    image: image5,
    isLogo: false,
    altKey: 'home.altPresentation',
    width: 1600,
    height: 1067,
  },
  {
    id: 6,
    image: image6,
    isLogo: false,
    altKey: 'home.altInAction',
    width: 1600,
    height: 1067,
  },
];

const carouselBreakpoints = {
  768: {
    itemsToShow: 2,
  },
  1120: {
    itemsToShow: 3,
  },
};

onMounted(() => {
  trackEvent('home_page_viewed', 'engagement', 'Homepage');
});

const trackFeatureClick = (feature: string) => {
  trackEvent('feature_card_clicked', 'engagement', feature);
};

const trackHeroCTA = (buttonName: string) => {
  trackEvent('hero_cta_clicked', 'engagement', buttonName);
};

const trackMainCTA = () => {
  trackEvent('main_cta_clicked', 'engagement', 'Contact CTA');
};
</script>

<style scoped>
.home-page {
  overflow: clip;
}

.hero {
  position: relative;
  padding: clamp(3rem, 5vw, 5.5rem) 0 clamp(4rem, 7vw, 7rem) 0;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 13% 20%, rgba(197, 141, 48, 0.18) 0%, rgba(197, 141, 48, 0) 40%),
    radial-gradient(circle at 88% 14%, rgba(47, 98, 133, 0.15) 0%, rgba(47, 98, 133, 0) 45%);
  pointer-events: none;
}

.hero-grid {
  position: relative;
  display: grid;
  gap: clamp(1.8rem, 3vw, 3.4rem);
}

@media (min-width: 1040px) {
  .hero-grid {
    grid-template-columns: 1.02fr 0.98fr;
    align-items: center;
  }
}

.hero-copy {
  position: relative;
  z-index: 1;
}

.hero-copy > * {
  animation: reveal-up 620ms var(--ease-out) both;
}

.hero-copy > :nth-child(2) {
  animation-delay: 70ms;
}

.hero-copy > :nth-child(3) {
  animation-delay: 140ms;
}

.hero-copy > :nth-child(4) {
  animation-delay: 210ms;
}

.hero-copy > :nth-child(5) {
  animation-delay: 280ms;
}

.hero-eyebrow {
  margin: 0 0 var(--spacing-3) 0;
  font-size: 0.84rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--color-primary-700);
  font-weight: var(--font-weight-bold);
}

.hero-title {
  margin: 0 0 var(--spacing-5) 0;
  font-size: clamp(2.7rem, 6vw, 5.1rem);
  line-height: 0.98;
  color: var(--color-primary-900);
}

.hero-subtitle {
  margin-bottom: var(--spacing-8);
  font-size: clamp(1.06rem, 1.7vw, 1.34rem);
  color: var(--color-text-secondary);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-8);
}

.hero-facts {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.7rem;
}

@media (min-width: 580px) {
  .hero-facts {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.hero-facts li {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.82rem 0.9rem;
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(16, 41, 59, 0.14);
  background: var(--surface-card);
}

.hero-facts strong {
  font-family: var(--font-family-display);
  font-size: 1.42rem;
  color: var(--color-primary-800);
  line-height: 1;
}

.hero-facts span {
  font-size: 0.82rem;
  color: var(--color-text-tertiary);
}

.hero-visual {
  position: relative;
  min-height: clamp(420px, 60vw, 640px);
}

.hero-image-frame {
  margin: 0;
  position: absolute;
  inset: 0 0 auto 0;
  border-radius: 1.6rem;
  overflow: hidden;
  border: 1px solid rgba(16, 41, 59, 0.15);
  box-shadow: var(--shadow-2xl);
}

.hero-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.hero-image-frame figcaption {
  margin: 0;
  position: absolute;
  left: 0;
  right: auto;
  bottom: 0;
  padding: 0.75rem 1rem;
  max-width: min(62%, 31rem);
  font-size: 0.79rem;
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(180deg, rgba(7, 17, 25, 0) 0%, rgba(7, 17, 25, 0.72) 100%);
  border-top-right-radius: 0.72rem;
  z-index: 2;
}

.next-concert-card {
  position: absolute;
  right: clamp(0.7rem, 2vw, 1.4rem);
  bottom: clamp(-0.2rem, 1.7vw, 1.1rem);
  width: min(360px, calc(100% - 1.4rem));
  padding: 1rem;
  border-radius: 1rem;
  background: var(--surface-card-strong);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-xl);
  backdrop-filter: blur(10px);
  z-index: 3;
}

.next-concert-label {
  margin: 0 0 0.42rem 0;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.69rem;
  color: var(--color-primary-700);
  font-weight: var(--font-weight-semibold);
}

.next-concert-title {
  margin: 0 0 0.75rem 0;
  font-family: var(--font-family-serif);
  font-size: 1.3rem;
  line-height: 1.13;
  color: var(--color-primary-900);
}

.next-concert-meta {
  margin: 0 0 0.9rem 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.48rem;
}

.next-concert-meta li {
  display: flex;
  gap: 0.58rem;
  align-items: flex-start;
  font-size: 0.86rem;
  color: var(--color-text-secondary);
}

.next-concert-meta i {
  margin-top: 0.1rem;
  color: var(--color-secondary-600);
}

.hero-badge-image {
  position: absolute;
  left: clamp(0.7rem, 1.6vw, 1.2rem);
  bottom: clamp(-2.2rem, -1vw, -0.6rem);
  width: clamp(92px, 11vw, 130px);
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 999px;
  border: 4px solid rgba(255, 255, 255, 0.88);
  box-shadow: var(--shadow-lg);
}

.section-eyebrow {
  margin: 0 0 0.6rem 0;
  font-size: 0.8rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.16em;
  line-height: 1.2;
  text-transform: uppercase;
  text-wrap: balance;
  color: var(--color-primary-700);
}

.editorial-section {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0.72) 100%);
}

.editorial-header {
  max-width: 720px;
  margin-bottom: clamp(1.8rem, 2.6vw, 2.5rem);
}

.editorial-header h2 {
  margin: 0 0 0.85rem 0;
  font-size: clamp(2rem, 4vw, 3rem);
  color: var(--color-primary-900);
}

.editorial-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 900px) {
  .editorial-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.editorial-card {
  padding: clamp(1.15rem, 2vw, 1.5rem);
  border-radius: 1rem;
  border: 1px solid rgba(16, 41, 59, 0.13);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.93) 0%, rgba(250, 247, 241, 0.92) 100%);
  box-shadow: var(--shadow-md);
}

.editorial-card-accent {
  background: linear-gradient(180deg, rgba(20, 56, 78, 0.96) 0%, rgba(17, 41, 60, 0.95) 100%);
  border-color: rgba(255, 255, 255, 0.14);
}

.editorial-card h3 {
  margin: 0 0 0.65rem 0;
  font-size: 1.5rem;
  color: var(--color-primary-900);
}

.editorial-card p {
  margin: 0 0 1rem 0;
}

.editorial-card a {
  display: inline-flex;
  align-items: center;
  gap: 0.48rem;
  font-weight: var(--font-weight-semibold);
}

.editorial-card a:hover {
  gap: 0.65rem;
}

.editorial-card-accent .editorial-icon {
  background: rgba(219, 174, 80, 0.18);
  color: var(--color-secondary-200);
}

.editorial-card-accent h3,
.editorial-card-accent p,
.editorial-card-accent a {
  color: #f2ece3;
}

.editorial-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border-radius: 0.74rem;
  background: rgba(16, 41, 59, 0.08);
  color: var(--color-primary-700);
  margin-bottom: 0.65rem;
}

.stats-section {
  padding-top: clamp(3rem, 5.2vw, 5rem);
  padding-bottom: clamp(3rem, 5.2vw, 5rem);
}

.stats-grid {
  display: grid;
  gap: 0.85rem;
}

@media (min-width: 820px) {
  .stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.stat-item {
  padding: 1.1rem;
  border-radius: 0.95rem;
  border: 1px solid rgba(16, 41, 59, 0.12);
  background: var(--surface-card);
  text-align: center;
}

.stat-number {
  margin: 0 0 0.18rem 0;
  font-size: clamp(2rem, 4vw, 3.1rem);
  line-height: 1;
  font-family: var(--font-family-display);
  color: var(--color-primary-800);
}

.stat-label {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
}

.gallery-section {
  background: linear-gradient(180deg, rgba(16, 41, 59, 0.04) 0%, rgba(255, 255, 255, 0.7) 100%);
}

.gallery-head {
  margin-bottom: 1.2rem;
}

.gallery-head h2 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
}

.gallery-shell {
  padding: 0.95rem;
  border-radius: 1rem;
  background: var(--surface-card);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-lg);
}

.modern-carousel {
  overflow: hidden;
}

:deep(.carousel__slide) {
  padding: 0.3rem;
}

.slide-content {
  aspect-ratio: 4 / 3;
  border-radius: 0.8rem;
  overflow: hidden;
  background: var(--color-neutral-200);
  box-shadow: var(--shadow-md);
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--duration-normal) var(--ease-out);
}

.slide-image.logo-image {
  object-fit: contain;
  background: #ffffff;
  padding: 1.5rem;
}

.slide-content:hover .slide-image:not(.logo-image) {
  transform: scale(1.04);
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 999px;
  background: var(--color-primary-700);
  color: #ffffff;
  transition: var(--transition-all);
}

:deep(.carousel__prev):hover,
:deep(.carousel__next):hover {
  background: var(--color-primary-800);
  transform: scale(1.04);
}

:deep(.carousel__pagination-button) {
  background: rgba(16, 41, 59, 0.3);
}

:deep(.carousel__pagination-button--active) {
  background: var(--color-secondary-500);
  width: 1.8rem;
  border-radius: 999px;
}

.cta-section {
  padding-top: clamp(3.6rem, 6vw, 5.5rem);
  padding-bottom: clamp(3.8rem, 6vw, 6.5rem);
}

.cta-card {
  border-radius: 1.25rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background:
    radial-gradient(circle at 12% 10%, rgba(219, 174, 80, 0.18) 0%, rgba(219, 174, 80, 0) 42%),
    linear-gradient(140deg, #0f293b 0%, #173c56 56%, #1f584d 100%);
  box-shadow: var(--shadow-2xl);
}

.cta-content {
  text-align: center;
  padding: clamp(2rem, 4vw, 3.2rem);
}

.cta-content .section-eyebrow {
  display: inline-flex;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.section-eyebrow-light {
  color: rgba(255, 255, 255, 0.76);
}

.cta-title {
  margin: 0 0 0.75rem 0;
  color: #ffffff;
  font-size: clamp(2.05rem, 4vw, 3.3rem);
}

.cta-text {
  margin: 0 auto 1.5rem auto;
  color: rgba(255, 255, 255, 0.88);
  max-width: 56ch;
}

@keyframes reveal-up {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1039px) {
  .hero-visual {
    min-height: 600px;
  }
}

@media (max-width: 767px) {
  .hero {
    padding-top: 2.2rem;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-visual {
    min-height: auto;
  }

  .hero-image-frame {
    position: relative;
    inset: auto;
    border-radius: 1rem;
  }

  .next-concert-card {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    width: 100%;
    margin-top: 0.85rem;
  }

  .hero-image-frame figcaption {
    right: 0;
    max-width: none;
    border-top-right-radius: 0;
  }

  .hero-badge-image {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-copy > * {
    animation: none;
  }
}
</style>
